/**
 * Route guard helpers for module routes
 * Used in module route files discovered by virtualRoutes.ts at build time
 */

import { redirect, notFound } from '@tanstack/react-router';
import { moduleRegistry } from './moduleRegistry';
import { canBypassModuleRoleRequirements, isAdminEquivalentRole } from '@/utils/authz';

/**
 * Get clientId from router context and location search params
 * Matches the logic in ModuleProvider
 */
export function getClientIdFromContext(context: any, location: any): string | null {
  // 1. Check admin client selection from URL search params
  const searchParams = location?.search as Record<string, unknown> | undefined;
  const adminClientId = searchParams?.adminClientId;
  const adminSelectedClientId = typeof adminClientId === 'string' && adminClientId.trim().length > 0 
    ? adminClientId 
    : null;
  
  // 2. Get from auth payload
  const authClientId = context?.auth?.clientId || null;
  
  // 3. Use admin selection if available, otherwise use auth clientId
  return adminSelectedClientId || authClientId;
}

/**
 * Create a beforeLoad guard for module routes.
 * Checks module registration, enablement, authentication, and role requirements.
 *
 * Admin-equivalent users (ADMIN, DEVELOPER) bypass `requiredRoles` and
 * `requiredTenantRoles` via {@link canBypassModuleRoleRequirements}. Module
 * definitions may list `requiredRoles: ['USER', 'ADMIN']` without DEVELOPER;
 * bypass is intentional.
 *
 * `requiredRoles` and `requiredTenantRoles` default to the module
 * definition's lists, so route files only pass them to override.
 *
 * Note: ModuleProvider loads modules before routes render, so enablement
 * can be safely checked here.
 */
export function createModuleRouteGuard(moduleId: string, routeMeta?: {
  requiresAuth?: boolean;
  requiredRoles?: string[];
  requiredTenantRoles?: string[];
}) {
  return ({ context, location }: any) => {
    // Check if module is registered
    const moduleDefinition = moduleRegistry.getDefinition(moduleId);
    if (!moduleDefinition) {
      throw notFound();
    }

    const requiredRoles = routeMeta?.requiredRoles ?? moduleDefinition.requiredRoles;
    const requiredTenantRoles = routeMeta?.requiredTenantRoles ?? moduleDefinition.requiredTenantRoles;
    
    // Check authentication if required
    if (routeMeta?.requiresAuth !== false) {
      if (!context?.auth) {
        throw redirect({
          to: '/login',
          search: { redirect: location.href } as any
        });
      }
    }
    
    // Get clientId for enablement check
    const clientId = getClientIdFromContext(context, location);
    const isAdmin = isAdminEquivalentRole(context?.auth?.role);
    
    // Check module enablement
    // For admins without client selection, allow access (they can select a client)
    // For others, check if module is enabled
    if (clientId) {
      const isEnabled = moduleRegistry.isEnabled(moduleId, clientId);
      if (!isEnabled && !isAdmin) {
        // Module not enabled for this client - redirect to default route
        throw redirect({
          to: getDefaultRoute(context?.auth?.role),
          search: { redirect: location.href } as any
        });
      }
    } else if (!isAdmin) {
      // No clientId and not admin - redirect to default route
      throw redirect({
        to: getDefaultRoute(context?.auth?.role),
        search: { redirect: location.href } as any
      });
    }
    
    // Check global role requirements
    if (requiredRoles && requiredRoles.length > 0) {
      const userRole = context?.auth?.role;
      if (!userRole || !requiredRoles.includes(userRole)) {
        // Allow ADMIN to access all routes
        if (!canBypassModuleRoleRequirements(userRole)) {
          throw notFound();
        }
      }
    }
    
    // Check tenant role requirements
    if (requiredTenantRoles && requiredTenantRoles.length > 0) {
      const tenantRoles = context?.auth?.tenantRoles || [];
      const hasRequiredRole = requiredTenantRoles.some(role => 
        tenantRoles.includes(role)
      );
      if (!hasRequiredRole) {
        // Allow ADMIN to access all routes
        if (!canBypassModuleRoleRequirements(context?.auth?.role)) {
          throw notFound();
        }
      }
    }
  };
}

/**
 * Get default route based on user role
 */
function getDefaultRoute(role?: string): string {
  switch (role) {
    case 'ADMIN':
    case 'DEVELOPER':
      return '/admin';
    case 'CUSTOMER':
      return '/client';
    case 'USER':
    default:
      return '/manage';
  }
}
